/**
 * CashDrawerSecuritySettings — Umbrales globales de efectivo en gaveta (multicaja).
 *
 * QUÉ HACE: Persiste en localStorage al cambiar interruptores o al editar montos (debounce).
 * El PIN cada N ventas vive en `CajaAdminSalesGateSettings` (misma persistencia, UI aparte).
 */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Banknote } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { FloatingLabelInput } from "@/components/ui/FloatingLabelInput";
import { SwitchRow } from "@/components/ui/Switch";
import { formatNumberInput, normalizeMoneyInput } from "@/lib/utils/formatters";
import {
  loadCashDrawerSecurityConfig,
  normalizeCashDrawerSecurity,
  persistCashDrawerSecurityConfig,
} from "@/features/caja/lib/cashDrawerSecurityConfig";

const SECTION_LABEL =
  "text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-slate-500 mb-2 px-0.5";

/** Despliegue suave de campos bajo el switch (CSS grid 0fr → 1fr). */
const EXPAND_GRID =
  "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none";

function applyLoadedConfig(
  businessId: string | undefined | null,
  setEnabled: (v: boolean) => void,
  setWarnStr: (v: string) => void,
  setBlockStr: (v: string) => void,
  setBypassStr: (v: string) => void,
) {
  const n = normalizeCashDrawerSecurity(
    loadCashDrawerSecurityConfig(businessId),
  );
  setEnabled(n.enabled);
  setWarnStr(formatNumberInput(n.warnThresholdCop || 0));
  setBlockStr(formatNumberInput(n.blockThresholdCop || 0));
  setBypassStr(String(n.bypassTtlMinutes || 30));
}

export function CashDrawerSecuritySettings() {
  const businessId = useAuthStore((s) => s.user?.businessId);
  const [enabled, setEnabled] = useState(false);
  const [warnStr, setWarnStr] = useState("");
  const [blockStr, setBlockStr] = useState("");
  const [bypassStr, setBypassStr] = useState("30");
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(false);
    applyLoadedConfig(
      businessId,
      setEnabled,
      setWarnStr,
      setBlockStr,
      setBypassStr,
    );
    setHydrated(true);
  }, [businessId]);

  useEffect(() => {
    const onExternal = () =>
      applyLoadedConfig(
        businessId,
        setEnabled,
        setWarnStr,
        setBlockStr,
        setBypassStr,
      );
    window.addEventListener("altenot:cash-drawer-security", onExternal);
    return () =>
      window.removeEventListener("altenot:cash-drawer-security", onExternal);
  }, [businessId]);

  useEffect(() => {
    if (!hydrated) return;
    const id = window.setTimeout(() => {
      const full = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      const next = normalizeCashDrawerSecurity({
        ...full,
        enabled,
        warnThresholdCop: Number(normalizeMoneyInput(warnStr, 0)) || 0,
        blockThresholdCop: Number(normalizeMoneyInput(blockStr, 0)) || 0,
        bypassTtlMinutes: Number(normalizeMoneyInput(bypassStr, 0)) || 30,
      });
      const prev = normalizeCashDrawerSecurity(
        loadCashDrawerSecurityConfig(businessId),
      );
      if (JSON.stringify(prev) === JSON.stringify(next)) return;
      persistCashDrawerSecurityConfig(businessId, next);
      setWarnStr(formatNumberInput(next.warnThresholdCop));
      setBlockStr(formatNumberInput(next.blockThresholdCop));
      setBypassStr(String(next.bypassTtlMinutes));
    }, 420);
    return () => window.clearTimeout(id);
  }, [businessId, enabled, warnStr, blockStr, bypassStr, hydrated]);

  return (
    <div className="space-y-0">
      <SwitchRow
        size="sm"
        icon={
          <Banknote size={16} className="text-amber-700 dark:text-amber-300" />
        }
        label="Máximo de efectivo en caja (sin contar la base)."
        checked={enabled}
        onChange={(v) => {
          if (!enabled && v) {
            queueMicrotask(() =>
              toast.success(
                "Control por efectivo estimado activado. Los umbrales ya aplican en todas las cajas.",
              ),
            );
          } else if (enabled && !v) {
            queueMicrotask(() =>
              toast("Tope de efectivo desactivado en este negocio."),
            );
          }
          setEnabled(v);
        }}
      />

      <div
        className={`${EXPAND_GRID} ${enabled ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
        aria-hidden={!enabled}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="space-y-2 pt-3">
            <div>
              <p className={SECTION_LABEL}>Umbrales (COP)</p>
              <div className="space-y-2">
                <FloatingLabelInput
                  label="Aviso (amarillo)"
                  hint="Desde qué estimado avisar"
                  disabled={!enabled}
                  value={warnStr}
                  onChange={(e) =>
                    setWarnStr(
                      formatNumberInput(normalizeMoneyInput(e.target.value, 0)),
                    )
                  }
                />
                <FloatingLabelInput
                  label="Tope máximo (bloqueo)"
                  hint="Efectivo estimado; arriba: bloqueo"
                  disabled={!enabled}
                  value={blockStr}
                  onChange={(e) =>
                    setBlockStr(
                      formatNumberInput(normalizeMoneyInput(e.target.value, 0)),
                    )
                  }
                />
              </div>
            </div>

            <FloatingLabelInput
              label="Minutos extra (este equipo)"
              hint="5 a 240"
              disabled={!enabled}
              value={bypassStr}
              onChange={(e) =>
                setBypassStr(normalizeMoneyInput(e.target.value, 0))
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
}
